// PayloadFields — 12종 item 타입별 payload 필드 인라인 편집 렌더링 (U43).
//
// 필드 정의는 constants/itemFields.ts (서버 itemTypes.ts 스키마와 1:1).
// 저장 시 onSave(fieldKey, value) 호출 — value는 서버 형식으로 변환된 값
// (number·boolean·string[]), undefined면 키 제거(선택 필드 비우기).
// 정의에 없는 기존 payload 키도 숨기지 않고 정의 필드 뒤에 표시한다 ('type' 제외).

import { CopyButton } from '../CopyButton';
import { InlineEdit } from '../../ui/InlineEdit';
import type { InlineEditType } from '../../ui/InlineEdit';
import {
  fromFormString,
  getItemFields,
  isMultilineKind,
  toDisplayString,
  toFormString,
  type FieldKind,
  type ItemFieldMeta,
} from '../../constants/itemFields';
import type { ItemType } from '../../api/items';

// ─── 타입 ─────────────────────────────────────────────────────

export interface PayloadFieldsProps {
  /** item 타입 */
  itemType: ItemType | string;
  /** 복호화된 payload 객체 */
  payload: Record<string, unknown>;
  /**
   * 필드 저장 콜백.
   * key: payload 필드명, value: 서버 형식 값 (undefined = 키 제거)
   */
  onSave: (key: string, value: unknown) => Promise<void>;
  /** 비활성화 여부 */
  disabled?: boolean;
}

// ─── 헬퍼 ─────────────────────────────────────────────────────

/** 정의 밖 키의 민감 여부 (이름 기반) */
function isSensitiveKey(key: string): boolean {
  return /password|secret|key|token|pwd|pass|private/i.test(key);
}

/** FieldKind → InlineEdit type */
function inlineTypeOf(kind: FieldKind): InlineEditType {
  if (kind === 'secret' || kind === 'secret-multiline') return 'password';
  if (kind === 'number') return 'number';
  return 'text';
}

/** 정의 밖 키 값을 문자열로 */
function toLooseString(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v === null || v === undefined) return '';
  return JSON.stringify(v);
}

/** select·boolean 입력 힌트 (인라인 편집 placeholder) */
function hintOf(field: ItemFieldMeta): string {
  if (field.kind === 'select') return (field.options ?? []).map((o) => o.value).join(' / ');
  if (field.kind === 'boolean') return 'true / false';
  if (field.kind === 'date') return 'YYYY-MM-DD';
  return `${field.label} 입력`;
}

// ─── 컴포넌트 ─────────────────────────────────────────────────

/**
 * PayloadFields — 타입별 payload 필드를 인라인 편집 가능하게 렌더링.
 * 정의 필드(값 없는 선택 필드는 생략) → 정의 밖 기존 키 순으로 표시.
 */
export function PayloadFields({ itemType, payload, onSave, disabled = false }: PayloadFieldsProps) {
  const fields = getItemFields(itemType);
  const knownKeys = new Set(fields.map((f) => f.key));

  // 정의 밖 키 (배열·객체 포함 단순 표시, 'type' 판별자는 제외)
  const extraEntries = Object.entries(payload).filter(
    ([k, v]) => k !== 'type' && !knownKeys.has(k) && v !== undefined && v !== null && v !== '',
  );

  const rows = fields.filter((f) => {
    const v = payload[f.key];
    const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    return !(f.optional && empty);
  });

  if (rows.length === 0 && extraEntries.length === 0) {
    return <p className="muted">페이로드 없음</p>;
  }

  return (
    <dl className="kv">
      {rows.map((field) => {
        const raw = payload[field.key];
        const editValue = toFormString(field, raw);
        // select·boolean은 한국어 레이블로 표시하고 인라인 편집 불가 (✏ 수정 폼의 드롭다운 사용)
        const isChoice = field.kind === 'select' || field.kind === 'boolean';
        const shown = isChoice ? toDisplayString(field, raw) : editValue;
        return (
          <div key={field.key} className="kv__row">
            <dt>{field.label}</dt>
            <dd className="kv__dd--inline-edit">
              <InlineEdit
                value={shown}
                type={inlineTypeOf(field.kind)}
                multiline={isMultilineKind(field.kind)}
                placeholder={hintOf(field)}
                disabled={disabled || isChoice}
                ariaLabel={`${field.label} 편집`}
                validate={(v) => {
                  const r = fromFormString(field, v);
                  if (r.empty) return field.optional ? true : `${field.label}은(는) 필수입니다`;
                  return 'error' in r ? r.error : true;
                }}
                onSave={(v) => {
                  const r = fromFormString(field, v);
                  return onSave(field.key, r.empty ? undefined : 'value' in r ? r.value : v);
                }}
              />
              {field.copyable && editValue && <CopyButton value={editValue} />}
            </dd>
          </div>
        );
      })}

      {extraEntries.map(([key, raw]) => {
        const strVal = toLooseString(raw);
        const isScalar = typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean';
        return (
          <div key={key} className="kv__row">
            <dt title="타입 정의에 없는 기존 필드">{key}</dt>
            <dd className="kv__dd--inline-edit">
              <InlineEdit
                value={strVal}
                type={isSensitiveKey(key) ? 'password' : 'text'}
                placeholder={`${key} 입력`}
                // 배열·객체는 문자열 편집 시 형식이 깨지므로 읽기 전용
                disabled={disabled || !isScalar}
                ariaLabel={`${key} 편집`}
                onSave={(v) => onSave(key, v === '' ? undefined : v)}
              />
              {strVal && <CopyButton value={strVal} />}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
